import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MyAccountContainer } from '@components/organisms/MyAccountContainer';
import { Table } from '@components/atoms/Table';
import { Card } from '@components/atoms/Card';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { Space } from '@components/atoms/Space';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { SITE_URLS } from '@constants';
import { formatPrice } from '@utils/format';
import { useData } from '@hooks/useData';
import { useGetUserPoints } from '@services/useGetUserPoints';

export const MyAccountPointsHistory: FC = () => {
    const { request: requestUserPoints, loading } = useGetUserPoints();
    const { userPoints } = useData();
    const { t } = useTranslation();

    const translations = {
        title: t('my-points-component.metatags.title'),
        activePoints: t('my-points-component.active-points'),
        totalPoints: t('my-points-component.total-points'),
    };

    useEffect(() => {
        !loading && requestUserPoints();
    }, []);

    return (
        <MyAccountContainer title={translations.title}>
            <div className="flex flex-col">
                <Space spaceBreakpoints={{ xs: { space: 30 } }} />
                <Typography fontSize="16" tagVariant={TypographyTagVariants.span} fontWeight="300">
                    <>{`${translations.activePoints}: ${userPoints?.data?.points}`}</>
                    <br />
                    <>{`${translations.totalPoints}: ${userPoints?.data?.total_earned}`}</>
                </Typography>
                <Space spaceBreakpoints={{ xs: { space: 30 } }} />
                <PointsHistoryTable />
            </div>
        </MyAccountContainer>
    );
};

const PointsHistoryTable: FC = () => {
    const { t } = useTranslation();
    const { myPoints } = useData();

    const translations = {
        tableHeaderOrderCode: t('my-points-component.headers.order-code'),
        tableHeaderPurchaseValue: t('my-points-component.headers.purchase-value'),
        tableHeaderPointsEarned: t('my-points-component.headers.points-earned'),
        tableHeaderVoucher: t('my-points-component.headers.voucher'),
        tableHeaderReason: t('my-points-component.headers.reason'),
        tableHeaderDate: t('my-points-component.headers.date'),
        expired: t('my-points-component.expired'),
    };

    return (
        <Card classes="mb-0 w-full">
            <Table
                borderless={true}
                headerCols={[
                    { classes: 'text-start', text: translations.tableHeaderOrderCode },
                    { classes: 'text-end', text: translations.tableHeaderPurchaseValue, hideOnMobile: true },
                    { classes: 'text-end', text: translations.tableHeaderPointsEarned },
                    { classes: 'text-center', text: translations.tableHeaderVoucher, hideOnMobile: true },
                    { classes: 'text-center', text: translations.tableHeaderReason, hideOnMobile: true },
                    { classes: 'text-center', text: translations.tableHeaderDate },
                ]}
                contentRows={(myPoints?.data || []).map((point) => {
                    return [
                        {
                            classes: 'text-start',
                            content: (
                                <Anchor
                                    href={SITE_URLS.myOrderDetail.replace(':uuid', `${point.order?.uuid}`)}
                                    mode={AnchorMode.none}>
                                    <Typography fontSize="14" tagVariant={TypographyTagVariants.span}>
                                        {point.order?.code}
                                    </Typography>
                                </Anchor>
                            ),
                        },
                        {
                            classes: 'text-end',
                            content: (
                                <Typography fontSize="14" tagVariant={TypographyTagVariants.span}>
                                    {point.purchase_value ? formatPrice(Number(point.purchase_value)) : ''}
                                </Typography>
                            ),
                        },
                        {
                            classes: 'text-end',
                            content: (
                                <Typography fontSize="14" tagVariant={TypographyTagVariants.span}>
                                    {point.points_earned ? (
                                        <>
                                            <span className="text-success">+ {point.points_earned}</span>

                                            {point.expired && (
                                                <span className="text-muted">
                                                    <br />({translations.expired})
                                                </span>
                                            )}
                                        </>
                                    ) : point.points_spent ? (
                                        <span className="text-danger">- {point.points_spent}</span>
                                    ) : point.points_expired ? (
                                        <span className="text-muted">- {point.points_expired}</span>
                                    ) : (
                                        ''
                                    )}
                                </Typography>
                            ),
                        },
                        {
                            classes: 'text-center',
                            content: (
                                <Typography fontSize="14" tagVariant={TypographyTagVariants.span}>
                                    {point.voucher_code || ''}
                                </Typography>
                            ),
                        },
                        {
                            classes: 'text-center',
                            content: (
                                <Typography fontSize="14" tagVariant={TypographyTagVariants.span}>
                                    {point.reason || ''}
                                </Typography>
                            ),
                        },
                        {
                            classes: 'text-center',
                            content: (
                                <Typography fontSize="14" tagVariant={TypographyTagVariants.span}>
                                    {point.date}
                                </Typography>
                            ),
                        },
                    ];
                })}
            />
        </Card>
    );
};
